/**
 * The non-payment part of a mosque shop.  Payments deliberately stay outside
 * this module: an order is created as a draft and a later payment adapter may
 * authorise it, never the other way around.
 *
 * Everything here is behind the `store` capability (capabilities.js), which is
 * off and not entitled for any mosque until the store has its own review.
 */
const PRODUCT_STATES = ['draft', 'published', 'archived'];
/**
 * Whether a PUBLISHED product can be had right now, as staff say it (store iteration 3). Not stock: nothing counts
 * down, nothing is reserved. "unavailable" keeps the product in the catalogue, marked, and out of any order.
 */
const AVAILABILITY = ['available', 'unavailable'];
/** A picture's description, read aloud by a screen reader: one or two sentences, not a caption essay. */
const MAX_IMAGE_ALT = 250;
/** Whole øre, 0 to 100 000 000 (1 000 000 kr). Free (0) is allowed: a free course or ticket. */
const MAX_PRICE_ORE = 100_000_000;
const MAX_TITLE = 120;
const MAX_DESCRIPTION = 2000;
const MAX_LINES = 50;
const MAX_QUANTITY = 100;
/** Products per mosque, archived ones included: each can carry a picture, and the database is the mosque's own. */
const MAX_PRODUCTS = 500;

/**
 * A price as the API received it: a JSON integer and nothing else. A missing price, a string ("25000"), a boolean
 * or a fraction is refused — never read as 0 kr, which would quietly make a product free.
 */
function validPriceOre(v) {
  return typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= MAX_PRICE_ORE;
}

function product(input = {}) {
  const src = input && typeof input === 'object' ? input : {};
  const title = typeof src.title === 'string' ? src.title.trim() : '';
  const description = typeof src.description === 'string' ? src.description.trim() : '';
  const state = src.state === undefined ? 'draft' : src.state;
  const availability = src.availability === undefined ? 'available' : src.availability;
  const errors = {};
  if (!title) errors.title = 'Oppgi et produktnavn.';
  else if (title.length > MAX_TITLE) errors.title = `Produktnavnet kan ha maks ${MAX_TITLE} tegn.`;
  if (src.description !== undefined && src.description !== null && typeof src.description !== 'string') errors.description = 'Beskrivelsen må være tekst.';
  else if (description.length > MAX_DESCRIPTION) errors.description = `Beskrivelsen kan ha maks ${MAX_DESCRIPTION} tegn.`;
  if (!validPriceOre(src.priceOre)) errors.priceOre = 'Prisen må være et helt antall øre, fra 0 til 100 000 000 (1 000 000 kr).';
  if (!PRODUCT_STATES.includes(state)) errors.state = 'Ugyldig produktstatus.';
  if (!AVAILABILITY.includes(availability)) errors.availability = 'Velg om varen er tilgjengelig eller ikke.';
  // Counted stock (store checkout, 2026-10-18): optional — null (or left out) is not counted, as before.
  const stock = src.stock === undefined ? null : src.stock;
  if (!(stock === null || (typeof stock === 'number' && Number.isSafeInteger(stock) && stock >= 0 && stock <= 100_000))) errors.stock = 'Lager er et helt tall fra 0 til 100 000, eller tomt (telles ikke).';
  // Weight in grams (shop shipping, 2026-10-31): optional — null (or left out) is unknown, and a rate lookup counts
  // shipping.DEFAULT_WEIGHT_G for it. Never a fraction, never 0.
  const weightG = src.weightG === undefined ? null : src.weightG;
  if (!require('./shipping').validWeightG(weightG)) errors.weightG = 'Vekten er et helt antall gram fra 1 til 50 000, eller tomt (regnes som 500 g).';
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: {
    title, description: description || null, priceOre: src.priceOre, state, availability, stock, weightG,
  } };
}

/** Every field of a product, as staff send it. */
const PRODUCT_FIELDS = ['title', 'description', 'priceOre', 'state', 'availability'];

/**
 * Store iteration 4: one meaning per method (review P3).
 *   replaceProduct(body)          PUT — the whole product: every field must be named (description may be null). A
 *                                 field left out is an error, never a default: a missing state must not quietly
 *                                 unpublish a product, nor a missing description erase one.
 *   patchProduct(current, body)   PATCH — only the fields named change; the rest keep their values. A field that is
 *                                 not a product's is refused.
 * Both end in product(), so the same rules decide.
 */
function replaceProduct(input) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const missing = PRODUCT_FIELDS.filter((k) => !(k in src));
  if (missing.length) return { ok: false, errors: Object.fromEntries(missing.map((k) => [k, 'Feltet mangler: send hele varen, eller bruk PATCH for å endre en del.'])) };
  return product(src);
}
function patchProduct(current, input) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src || !Object.keys(src).length) return { ok: false, errors: { product: 'Ingenting å endre.' } };
  const unknown = Object.keys(src).filter((k) => !PRODUCT_FIELDS.includes(k) && k !== 'stock' && k !== 'weightG');
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  return product({ ...current, ...src });
}

/**
 * A picture's description, and whether it is decorative (store iteration 3). Staff must say which, explicitly:
 *   { decorative: true }                 pynt — nothing to describe; a screen reader skips it
 *   { decorative: false, alt: '…' }      meaningful — 1 to 250 characters, one line, read aloud
 * Neither, both, or a description on a decorative picture is refused: an unlabelled picture is the default this
 * exists to end. Whitespace runs collapse to one space; the text is the mosque's own Norwegian, never translated.
 */
function imageDescription(input = {}) {
  const src = input && typeof input === 'object' ? input : {};
  const alt = typeof src.alt === 'string' ? src.alt.replace(/\s+/g, ' ').trim() : '';
  if (src.alt !== undefined && src.alt !== null && typeof src.alt !== 'string') return { ok: false, errors: { alt: 'Bildebeskrivelsen må være tekst.' } };
  if (typeof src.decorative !== 'boolean') {
    return { ok: false, errors: { alt: 'Beskriv bildet, eller merk det som pynt (uten beskrivelse).' } };
  }
  if (src.decorative && alt) return { ok: false, errors: { alt: 'Et bilde merket som pynt har ingen beskrivelse. Fjern teksten, eller fjern merket.' } };
  if (!src.decorative && !alt) return { ok: false, errors: { alt: 'Skriv hva bildet viser, eller merk det som pynt.' } };
  if (alt.length > MAX_IMAGE_ALT) return { ok: false, errors: { alt: `Bildebeskrivelsen kan ha maks ${MAX_IMAGE_ALT} tegn.` } };
  if (/[\u0000-\u001f\u007f]/.test(alt)) return { ok: false, errors: { alt: 'Bildebeskrivelsen kan ikke ha kontrolltegn.' } };
  return { ok: true, value: { decorative: src.decorative, alt: src.decorative ? null : alt } };
}

function orderLines(lines) {
  if (!Array.isArray(lines) || !lines.length) return { ok: false, message: 'Ordren må ha minst én vare.' };
  if (lines.length > MAX_LINES) return { ok: false, message: 'Ordren har for mange varelinjer.' };
  const seen = new Set();
  for (const l of lines) {
    if (!l || typeof l.productId !== 'string' || !l.productId || l.productId.length > 64
        || typeof l.quantity !== 'number' || !Number.isInteger(l.quantity) || l.quantity < 1 || l.quantity > MAX_QUANTITY) {
      return { ok: false, message: `Hver varelinje må ha produkt og antall fra 1 til ${MAX_QUANTITY}.` };
    }
    if (seen.has(l.productId)) return { ok: false, message: 'Et produkt kan bare stå én gang i ordren.' };
    seen.add(l.productId);
  }
  return { ok: true };
}

/**
 * The order's lines priced from the products AS THEY ARE NOW, and their total. Every product must exist and be
 * published; the result is what the order snapshots (title and unit price per line), so a later edit of a product
 * never changes an order already made. The total must stay a safe integer.
 */
function priceOrder(lines, products) {
  const check = orderLines(lines);
  if (!check.ok) return check;
  const byId = new Map((products || []).map((p) => [p.id, p]));
  const priced = [];
  let total = 0;
  for (const l of lines) {
    const p = byId.get(l.productId);
    if (!p || p.state !== 'published') return { ok: false, message: 'Alle varer må finnes og være publisert.' };
    if (p.availability && p.availability !== 'available') return { ok: false, message: `«${p.title}» er ikke tilgjengelig nå.` };
    if (!validPriceOre(p.priceOre)) return { ok: false, message: 'En vare har en ugyldig pris.' };
    total += p.priceOre * l.quantity;
    if (!Number.isSafeInteger(total)) return { ok: false, message: 'Ordresummen er for stor.' };
    priced.push({ productId: p.id, title: p.title, unitPriceOre: p.priceOre, quantity: l.quantity });
  }
  return { ok: true, lines: priced, totalOre: total };
}

/**
 * A price as STAFF type it, in kroner: "149", "149,50", "149.5", "1 250,00". Whole øre only (at most two decimals),
 * 0 to 1 000 000 kr. Anything else is refused — never guessed, never rounded.
 */
function parseKroner(text) {
  const s = String(text ?? '').trim().replace(/[\s\u00a0]/g, '');
  const m = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return { ok: false, message: 'Skriv prisen i kroner, for eksempel 149 eller 149,50.' };
  const ore = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || 0);
  return validPriceOre(ore) ? { ok: true, ore } : { ok: false, message: 'Prisen kan være fra 0 til 1 000 000 kr.' };
}

/**
 * What the public catalogue shows of a product: published ones only, no state, no dates. `available` says whether
 * it can be had now. Its picture is an address, a size and a description ({ path, width, height, alt }) or null —
 * never the bytes, and never a picture the caller did not vouch for as published. `alt` is null for a decorative
 * picture (a screen reader skips it), the staff's description otherwise.
 */
function catalogueView(p) {
  const im = p.image && typeof p.image.path === 'string'
    ? { path: p.image.path, width: p.image.width, height: p.image.height, alt: typeof p.image.alt === 'string' && p.image.alt ? p.image.alt : null } : null;
  const soldOut = p.stock === 0;
  // weightG (shop shipping): what the checkout weighs the basket by for a carrier rate; null = unknown (500 g).
  return { id: p.id, title: p.title, description: p.description || null, priceOre: p.priceOre,
    available: (p.availability === undefined || p.availability === 'available') && !soldOut, soldOut, image: im,
    weightG: typeof p.weightG === 'number' ? p.weightG : null };
}

module.exports = { PRODUCT_STATES, AVAILABILITY, MAX_IMAGE_ALT, imageDescription, PRODUCT_FIELDS, replaceProduct, patchProduct, MAX_PRICE_ORE, MAX_TITLE, MAX_DESCRIPTION, MAX_LINES, MAX_QUANTITY, MAX_PRODUCTS,
  validPriceOre, product, orderLines, priceOrder, catalogueView, parseKroner };
